/**
 * apps/web/src/api/client.ts
 * -------------------------------------------------------
 * Purpose:
 *   Tiny HTTP client used by React Router loaders/actions.
 *
 * High-level data flow:
 *   - Loaders/actions call `api.*` methods.
 *   - Each method performs a `fetch()` to the Express API (`/api/*`) with `credentials: 'include'`
 *     so the Better Auth session cookie is sent automatically.
 *   - Responses are validated with zod schemas from `packages/contracts` to keep runtime data safe.
 */

import { z } from 'zod';
import {
  AnalyticsSummarySchema,
  ApiErrorSchema,
  AuditDetailSchema,
  AuditListResponseSchema,
  AuditListQuerySchema,
  AuditReviewActivityResponseSchema,
  AuditReviewStateSchema,
  MeSchema,
  type AnalyticsSummary,
  type AuditDetail,
  type AuditListResponse,
  type AuditReviewActivityResponse,
  type AuditReviewState,
  type Me,
  type UpsertAuditReviewStateBody,
} from '@tinypro-audit/contracts';

/** Error thrown by the API client when a request fails. */
export class ApiClientError extends Error {
  /** HTTP status code from the response. */
  public readonly status: number;

  /** Standard API error code (when available). */
  public readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** Build a query string from an object of optional parameters. */
function toQueryString(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

/** Perform a fetch and parse the JSON response using the provided zod schema. */
export async function fetchJson<T>(url: string, init: RequestInit, schema: z.ZodType<T>): Promise<T> {
  const res = await fetch(url, {
    ...init,
    credentials: 'include',
    cache: 'no-store',
    headers: {
      'content-type': 'application/json',
      'X-TinyPro-Admin': '1',
      ...(init.headers ?? {}),
    },
  });

  // Success path: parse as JSON and validate.
  if (res.ok) {
    const json = await res.json();
    return schema.parse(json);
  }

  // Error path: try parsing the standard ApiError shape.
  const text = await res.text();
  try {
    const json = JSON.parse(text);
    const apiErr = ApiErrorSchema.parse(json);
    throw new ApiClientError(apiErr.message, res.status, apiErr.code);
  } catch (err) {
    if (err instanceof ApiClientError) throw err;
    // If parsing fails (e.g., Better Auth errors), throw a generic message with status.
    throw new ApiClientError(text || 'Request failed', res.status);
  }
}

/** Perform a fetch for Better Auth endpoints which may not follow the ApiError shape. */
async function fetchAuth(url: string, init: RequestInit): Promise<void> {
  const res = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });

  if (res.ok) return;

  // Best-effort error extraction for Better Auth responses.
  const text = await res.text();
  throw new ApiClientError(text || 'Authentication failed', res.status);
}

/** Public API client used by route loaders/actions. */
export const api = {
  /** Fetch current user + org + role (requires an active session). */
  async getMe(): Promise<Me> {
    return fetchJson('/api/me', { method: 'GET' }, MeSchema);
  },

  /** Fetch dashboard analytics summary. */
  async getAnalyticsSummary(range?: string): Promise<AnalyticsSummary> {
    const qs = toQueryString({ range });
    return fetchJson(`/api/analytics/summary${qs}`, { method: 'GET' }, AnalyticsSummarySchema);
  },

  /** Fetch admin-only review activity derived from audit_review_states. */
  async getAdminAuditReviewActivity(range?: string): Promise<AuditReviewActivityResponse> {
    const qs = toQueryString({ range });
    return fetchJson(
      `/api/admin/audit-review-states${qs}`,
      { method: 'GET' },
      AuditReviewActivityResponseSchema,
    );
  },

  /** List audits (supports search + filter presets). */
  async listAudits(query: z.infer<typeof AuditListQuerySchema>): Promise<AuditListResponse> {
    const qs = toQueryString(query);
    return fetchJson(`/api/audits${qs}`, { method: 'GET' }, AuditListResponseSchema);
  },

  /** Fetch full audit details for the audit viewer. */
  async getAudit(auditId: string): Promise<AuditDetail> {
    return fetchJson(`/api/audits/${encodeURIComponent(auditId)}`, { method: 'GET' }, AuditDetailSchema);
  },

  /** Upsert the local manual-review state for an audit. */
  async upsertAuditReviewState(auditId: string, body: UpsertAuditReviewStateBody): Promise<AuditReviewState> {
    return fetchJson(`/api/audits/${encodeURIComponent(auditId)}/review-state`, { method: 'PUT', body: JSON.stringify(body) }, AuditReviewStateSchema);
  },

  /** Sign in with email + password using Better Auth endpoints. */
  async signInEmail(email: string, password: string): Promise<void> {
    // Better Auth email/password sign-in endpoint.
    await fetchAuth('/api/auth/sign-in/email', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  /** Sign out and invalidate the server-side session. */
  async signOut(): Promise<void> {
    await fetchAuth('/api/auth/sign-out', { method: 'POST', body: JSON.stringify({}) });
  },
};
